import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from "../includes/sidebar";
import { useTranslation } from 'react-i18next';
import { Topbar } from "../includes/topbar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import {
    MoreHorizontal,
    Plus,
    Search,
    Eye,
    UserCheck,
    UserX,
    Mail,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Loader2,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Download,
    Settings,
    Calendar,
    Shield,
    Phone,
    CreditCard,
    Wallet,
    CalendarDays,
    UserCog
} from "lucide-react";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { comptesApi, clientsApi, adminApi } from '../../api';
import { agentsApi } from '../../api/agents.api';
import type { Compte, Client } from '../../api/clients.api';
import { CreateClientModal } from '../../components/modals/CreateClientModal';
import { EditClientModal } from '../../components/modals/EditClientModal';
import { AssignAgentModal } from '../../components/modals/AssignAgentModal';
import { toast } from '../../hooks/use-toast';

// Status badge colors
const statusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
        case 'actif':
            return 'default';
        case 'pending':
            return 'secondary';
        case 'inactif':
        case 'suspendu':
        case 'frozen':
            return 'destructive';
        default:
            return 'outline';
    }
};

const typeBadgeVariant = (type: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (type) {
        case 'BS':
            return 'default';
        case 'SOL':
            return 'secondary';
        default:
            return 'outline';
    }
};

// Balance display component with conditional expand
interface BalanceCellProps {
    compte: Compte;
    formatBalance: (value: number | undefined) => string;
}

function BalanceCell({ compte, formatBalance }: BalanceCellProps) {
    const [isOpen, setIsOpen] = useState(false);

    // Determine which balances to show based on type
    const isBs = compte.type === 'BS';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _isSol = compte.type === 'SOL'; // SOL shows only soldeS
    const hasPlanSol = !!compte.planSol;

    // Primary balance
    const primaryBalance = isBs ? compte.soldeBS : compte.soldeS;
    const primaryLabel = isBs ? 'BS' : 'SOL';

    // Secondary balance (only for BS with planSol)
    const hasSecondary = isBs && hasPlanSol && Number(compte.soldePL) > 0;

    return (
        <div className="flex items-center gap-1">
            <div className="text-right font-mono min-w-[80px]">
                <div className="flex items-center justify-end gap-1">
                    <span className="text-xs text-gray-400">{primaryLabel}:</span>
                    <span>{formatBalance(primaryBalance)}</span>
                </div>

                {/* Expanded secondary balance */}
                {hasSecondary && isOpen && (
                    <div className="flex items-center justify-end gap-1 text-sm text-blue-600 mt-1">
                        <span className="text-xs text-gray-400">PL:</span>
                        <span>{formatBalance(compte.soldePL)}</span>
                    </div>
                )}
            </div>

            {/* Expand button - only show if has secondary */}
            {hasSecondary && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {isOpen ? (
                        <ChevronUp className="h-3 w-3" />
                    ) : (
                        <ChevronDown className="h-3 w-3" />
                    )}
                </Button>
            )}
        </div>
    );
}

interface DropdownMenuActionsProps {
    compte: Compte;
    handleViewDetails: (compte: Compte) => void;
    handleDeactivateCompte: (compte: Compte) => void;
    handleActivateCompte: (compte: Compte) => void;
    handleEditClient: (client: Client, compte: Compte) => void;
    handleAddAccount: (client: Client) => void;
    handleSendPinLink: (client: Client) => void;
    openSuspendDialog: (client: Client) => void;
    handleAssignAgent: (compte: Compte) => void;
    isSendingPin: string | null;
    t: any;
}

function DropdownMenuActions({
    compte,
    handleViewDetails,
    handleDeactivateCompte,
    handleActivateCompte,
    handleEditClient,
    handleAddAccount,
    handleSendPinLink,
    openSuspendDialog,
    handleAssignAgent,
    isSendingPin,
    t
}: DropdownMenuActionsProps) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-slate-100 rounded-full">
                    <span className="sr-only">{t('accounts:clients.actions.openMenu')}</span>
                    <MoreHorizontal className="h-4 w-4 text-slate-500" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs font-bold text-slate-500 uppercase tracking-wider px-3 py-2">
                    {t('accounts:clients.table.actions')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleViewDetails(compte)} className="cursor-pointer">
                    <Eye className="mr-2 h-4 w-4 text-slate-400" />
                    <span>{t('accounts:clients.actions.viewDetails')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                    onClick={() => compte.statut === 'actif' ? handleDeactivateCompte(compte) : handleActivateCompte(compte)}
                    className="cursor-pointer"
                >
                    {compte.statut === 'actif' ? (
                        <>
                            <UserX className="mr-2 h-4 w-4 text-slate-400" />
                            <span>{t('accounts:clients.actions.deactivate')}</span>
                        </>
                    ) : (
                        <>
                            <UserCheck className="mr-2 h-4 w-4 text-slate-400" />
                            <span>{t('accounts:clients.actions.activate')}</span>
                        </>
                    )}
                </DropdownMenuItem>
                {compte.client && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleEditClient(compte.client!, compte)} className="cursor-pointer">
                            <Settings className="mr-2 h-4 w-4 text-slate-400" />
                            <span>{t('accounts:clients.actions.edit')}</span>
                        </DropdownMenuItem>
                    </>
                )}
                {compte.client && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleAddAccount(compte.client!)} className="cursor-pointer">
                            <Plus className="mr-2 h-4 w-4 text-slate-400" />
                            <span>{t('accounts:clients.actions.newAccount')}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => handleSendPinLink(compte.client!)}
                            disabled={isSendingPin === compte.client?.userId}
                            className="cursor-pointer"
                        >
                            {isSendingPin === compte.client?.userId ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Mail className="mr-2 h-4 w-4 text-slate-400" />
                            )}
                            <span>{t('accounts:clients.actions.sendPin')}</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={() => openSuspendDialog(compte.client!)}
                            className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer"
                        >
                            <UserX className="mr-2 h-4 w-4" />
                            <span className="font-semibold">{t('accounts:clients.actions.suspendClient')}</span>
                        </DropdownMenuItem>
                    </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={() => handleAssignAgent(compte)}
                    className="cursor-pointer"
                >
                    <UserCog className="mr-2 h-4 w-4 text-slate-400" />
                    <span>{compte.agentId ? 'Chanje / Libere Ajan' : 'Asiye Ajan'}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function ClientsPage() {
    const { t } = useTranslation(['accounts', 'common']);
    // State - now using Compte with client relation
    const [comptes, setComptes] = useState<Compte[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalComptes, setTotalComptes] = useState(0);
    const [statusFilter, setStatusFilter] = useState("Tous");
    const [typeFilter, setTypeFilter] = useState("Tous");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Modal state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
    const [compteToEdit, setCompteToEdit] = useState<Compte | null>(null);
    const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
    const [selectedClientForAccount, setSelectedClientForAccount] = useState<Client | null>(null);
    const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
    const [selectedCompteForDetails, setSelectedCompteForDetails] = useState<Compte | null>(null);
    const [isSuspendDialogOpen, setIsSuspendDialogOpen] = useState(false);
    const [clientToSuspend, setClientToSuspend] = useState<Client | null>(null);
    const [isSendingPin, setIsSendingPin] = useState<string | null>(null);
    const [isAssignAgentOpen, setIsAssignAgentOpen] = useState(false);
    const [agentMap, setAgentMap] = useState<Record<string, string>>({});
    const [compteForAssign, setCompteForAssign] = useState<Compte | null>(null);

    // Export state
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
    const [exportType, setExportType] = useState<'all' | 'BS' | 'SOL'>('all');
    const [isExporting, setIsExporting] = useState(false);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1); // Reset to page 1 on search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [typeFilter, statusFilter]);

    // Fetch comptes with server-side filtering
    const fetchComptes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await comptesApi.list({
                page: currentPage,
                limit: itemsPerPage,
                type: typeFilter !== "Tous" ? typeFilter : undefined,
                statut: statusFilter !== "Tous" ? statusFilter : undefined,
                search: debouncedSearch || undefined,
            });
            setComptes(response.comptes || []);
            setTotalComptes(response.total || 0);
        } catch (err: any) {
            const message = err.response?.data?.message || t('accounts:clients.table.noAccounts');
            setError(message);
            console.error('Fetch comptes error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemsPerPage, typeFilter, statusFilter, debouncedSearch, t]);

    // Initial load and refetch on filter/page change
    useEffect(() => {
        fetchComptes();
    }, [fetchComptes]);

    // Chaje lis ajan yon fwa pou afiche non yo nan badge
    useEffect(() => {
        agentsApi.getAll().then(res => {
            const map: Record<string, string> = {};
            for (const a of (res.agents || [])) {
                const nom = [a.client?.prenom, a.client?.nom].filter(Boolean).join(' ') || a.code;
                map[a.id] = nom;
            }
            setAgentMap(map);
        }).catch(() => {});
    }, []);

    // No more client-side filtering needed - server does it all
    const filteredComptes = comptes;

    // Pagination
    const totalPages = Math.ceil(totalComptes / itemsPerPage);

    // Actions
    const handleActivateCompte = async (compte: Compte) => {
        try {
            await comptesApi.activate(compte.id);
            toast({
                title: t('common:success'),
                description: t('accounts:clients.actions.activate') + ` ${compte.code}`,
                type: 'success',
            });
            fetchComptes();
        } catch (err: any) {
            toast({
                title: t('common:error'),
                description: err.response?.data?.message || t('accounts:unvalidated.status.error'),
                type: 'error',
            });
        }
    };

    const handleDeactivateCompte = async (compte: Compte) => {
        try {
            await comptesApi.deactivate(compte.id);
            toast({
                title: t('common:success'),
                description: t('accounts:clients.actions.deactivate') + ` ${compte.code}`,
                type: 'success',
            });
            fetchComptes();
        } catch (err: any) {
            toast({
                title: t('common:error'),
                description: err.response?.data?.message || t('accounts:unvalidated.status.error'),
                type: 'error',
            });
        }
    };

    const handleSuspendClient = async () => {
        if (!clientToSuspend) return;
        try {
            await clientsApi.suspendAll(clientToSuspend.id, 'Suspension administrative');
            toast({
                title: t('common:success'),
                description: `${clientToSuspend.prenom} ${clientToSuspend.nom} ` + t('accounts:clients.dialogs.suspend.confirm'),
                type: 'success',
            });
            setIsSuspendDialogOpen(false);
            setClientToSuspend(null);
            fetchComptes();
        } catch (err: any) {
            toast({
                title: t('common:error'),
                description: err.response?.data?.message || t('common:error'),
                type: 'error',
            });
        }
    };

    const handleAssignAgent = (compte: Compte) => {
        setCompteForAssign(compte);
        setIsAssignAgentOpen(true);
    };

    const openSuspendDialog = (client: Client) => {
        setClientToSuspend(client);
        setIsSuspendDialogOpen(true);
    };

    const handleViewDetails = (compte: Compte) => {
        setSelectedCompteForDetails(compte);
        setIsViewDetailsOpen(true);
    };

    const handleSendPinLink = async (client: Client) => {
        if (!client?.userId && !client?.telephone && !client?.email) {
            toast({
                title: 'Erè',
                description: 'Kliyan sa pa gen okenn idantifyan valid (userId, tel, oswa email)',
                type: 'error',
            });
            return;
        }
        setIsSendingPin(client.userId || client.id);
        try {
            await adminApi.resendPinSetup({
                userId: client.userId,
                telephone: client.telephone,
                email: client.email,
            });
            toast({
                title: t('common:success'),
                description: t('accounts:clients.actions.sendPin') + ` ${client.email || client.telephone}`,
                type: 'success',
            });
        } catch (err: any) {
            toast({
                title: t('common:error'),
                description: err.response?.data?.message || t('accounts:clients.actions.sendPin'),
                type: 'error',
            });
        } finally {
            setIsSendingPin(null);
        }
    };

    const handleEditClient = (client: Client, compte: Compte) => {
        setClientToEdit(client);
        setCompteToEdit(compte);
        setIsEditModalOpen(true);
    };

    const handleAddAccount = (client: Client) => {
        setSelectedClientForAccount(client);
        setIsAddAccountModalOpen(true);
    };

    // PDF Export function - exports accounts by type
    const handleExportPDF = async () => {
        setIsExporting(true);
        setIsExportDialogOpen(false);

        try {
            // Fetch all accounts of selected type (no pagination limit)
            const typeToFetch = exportType === 'all' ? undefined : exportType;
            const response = await comptesApi.list({
                page: 1,
                limit: 1000, // Get all
                type: typeToFetch,
            });

            const comptesToExport = response.comptes || [];

            if (comptesToExport.length === 0) {
                toast({
                    title: t('common:error'),
                    description: t('accounts:clients.export.noData', { type: exportType === 'all' ? '' : exportType }),
                    type: 'error',
                });
                setIsExporting(false);
                return;
            }

            // Dynamic import for jsPDF
            const { default: jsPDF } = await import('jspdf');
            const { default: autoTable } = await import('jspdf-autotable');

            const doc = new jsPDF();

            // Header with Bousanm branding
            doc.setFillColor(37, 99, 235); // Primary blue
            doc.rect(0, 0, 210, 35, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(24);
            doc.setFont('helvetica', 'bold');
            doc.text('BOUSANM', 15, 20);

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const typeLabel = exportType === 'all' ? 'Tout Kont' : `Kont ${exportType}`;
            doc.text(`Lis ${typeLabel}`, 15, 28);

            // Date and count
            doc.setFontSize(9);
            doc.text(`Dat: ${new Date().toLocaleDateString('fr-FR')}`, 150, 20);
            doc.text(`Total: ${comptesToExport.length} kont`, 150, 28);

            // Reset text color
            doc.setTextColor(0, 0, 0);

            // Table data
            const tableData = comptesToExport.map(compte => [
                compte.code,
                compte.client ? `${compte.client.prenom} ${compte.client.nom}` : '-',
                compte.client?.telephone || '-',
                compte.type,
                compte.devise,
                compte.statut
            ]);

            // Generate table
            autoTable(doc, {
                startY: 42,
                head: [['Kòd', 'Kliyan', 'Telefòn', 'Tip', 'Deviz', 'Statu']],
                body: tableData,
                theme: 'striped',
                headStyles: {
                    fillColor: [37, 99, 235],
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 9,
                },
                bodyStyles: {
                    fontSize: 8,
                },
                columnStyles: {
                    0: { cellWidth: 30 }, // kòd
                    1: { cellWidth: 50 }, // kliyan
                    2: { cellWidth: 35 }, // telefòn
                    3: { cellWidth: 20 }, // tip
                    4: { cellWidth: 20 }, // deviz
                    5: { cellWidth: 25 }, // statu
                },
                margin: { left: 15, right: 15 },
            });

            // Footer
            const pageCount = doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(128, 128, 128);
                doc.text(
                    `Paj ${i} sou ${pageCount} - Jenere pa Bousanm © ${new Date().getFullYear()}`,
                    105,
                    doc.internal.pageSize.height - 10,
                    { align: 'center' }
                );
            }

            // Save the PDF
            const typeSlug = exportType === 'all' ? 'tout' : exportType.toLowerCase();
            const filename = `bousanm-kont-${typeSlug}-${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(filename);

            toast({
                title: t('common:success'),
                description: t('accounts:clients.export.success', { count: comptesToExport.length, filename }),
                type: 'success',
            });
        } catch (err) {
            console.error('PDF export error:', err);
            toast({
                title: t('common:error'),
                description: t('accounts:clients.export.error'),
                type: 'error',
            });
        } finally {
            setIsExporting(false);
        }
    };

    // Format balance
    const formatBalance = (value: number | undefined): string => {
        if (value === undefined || value === null) return '0.00';
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(value));
    };

    // Format date
    const formatDate = (dateString: string | undefined): string => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden bg-slate-50/50">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-8 pt-4 sm:pt-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink className="capitalize">{t('dashboard:sidebar.sections.comptes')}</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="capitalize">{t('dashboard:sidebar.items.clients')}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                    <Card className="border-none shadow-sm">
                        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0 pb-4">
                            <div className="space-y-1">
                                <CardTitle className="text-xl font-bold">{t('accounts:clients.title')}</CardTitle>
                                <CardDescription className="text-xs sm:text-sm">
                                    {t('accounts:clients.description')}
                                </CardDescription>
                            </div>
                            <div className="flex flex-col xs:flex-row items-stretch sm:items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setIsExportDialogOpen(true)}
                                    disabled={isExporting}
                                    className="h-9 sm:h-8"
                                >
                                    {isExporting ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Download className="mr-2 h-4 w-4" />
                                    )}
                                    <span className="truncate">{t('accounts:clients.exportPdf')}</span>
                                </Button>
                                <Button size="sm" onClick={() => setIsCreateModalOpen(true)} className="h-9 sm:h-8 font-bold">
                                    <Plus className="mr-2 h-4 w-4" /> {t('accounts:clients.addClient')}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4 sm:p-6 pt-0">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center py-4 gap-3 bg-white/50 rounded-lg">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <Input
                                        placeholder={t('accounts:clients.searchPlaceholder')}
                                        className="pl-9 h-10 sm:h-9 bg-white border-slate-200"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-slate-100"
                                            onClick={() => setSearchTerm("")}
                                        >
                                            <UserX className="h-3 w-3" />
                                        </Button>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                                        <SelectTrigger className="flex-1 sm:w-[130px] h-10 sm:h-9 bg-white border-slate-200">
                                            <SelectValue placeholder={t('accounts:clients.filters.type')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Tous">{t('accounts:clients.filters.allTypes')}</SelectItem>
                                            <SelectItem value="BS">BS (Bousanm)</SelectItem>
                                            <SelectItem value="SOL">SOL (Sabotay)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                                        <SelectTrigger className="flex-1 sm:w-[130px] h-10 sm:h-9 bg-white border-slate-200">
                                            <SelectValue placeholder={t('accounts:clients.filters.status')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Tous">{t('accounts:clients.filters.allStatuses')}</SelectItem>
                                            <SelectItem value="actif">{t('common:status.active')}</SelectItem>
                                            <SelectItem value="pending">{t('common:status.pending')}</SelectItem>
                                            <SelectItem value="inactif">{t('common:status.inactive')}</SelectItem>
                                            <SelectItem value="frozen">{t('accounts:clients.filters.allStatuses') === 'Tout estati' ? 'Jele' : 'Frozen'}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Active filters display */}
                            {(searchTerm || typeFilter !== "Tous" || statusFilter !== "Tous") && (
                                <div className="flex flex-wrap items-center gap-2 pb-4">
                                    <span className="text-sm text-gray-500">{t('accounts:clients.filters.active')}</span>
                                    {searchTerm && (
                                        <Badge variant="secondary" className="cursor-pointer" onClick={() => setSearchTerm("")}>
                                            {t('accounts:clients.filters.search')}: "{searchTerm}" ×
                                        </Badge>
                                    )}
                                    {typeFilter !== "Tous" && (
                                        <Badge variant="secondary" className="cursor-pointer" onClick={() => setTypeFilter("Tous")}>
                                            {t('accounts:clients.filters.type')}: {typeFilter} ×
                                        </Badge>
                                    )}
                                    {statusFilter !== "Tous" && (
                                        <Badge variant="secondary" className="cursor-pointer" onClick={() => setStatusFilter("Tous")}>
                                            {t('accounts:clients.filters.status')}: {statusFilter} ×
                                        </Badge>
                                    )}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-red-500 h-auto p-0"
                                        onClick={() => {
                                            setSearchTerm("");
                                            setTypeFilter("Tous");
                                            setStatusFilter("Tous");
                                        }}
                                    >
                                        {t('accounts:clients.filters.clearAll')}
                                    </Button>
                                </div>
                            )}

                            {/* Error state */}
                            {error && (
                                <div className="flex items-center gap-2 p-4 mb-4 text-red-600 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="h-5 w-5" />
                                    <span>{error}</span>
                                    <Button variant="link" className="ml-auto text-red-600" onClick={fetchComptes}>
                                        {t('dashboard:errors.tryAgain')}
                                    </Button>
                                </div>
                            )}

                            {/* Loading state */}
                            {isLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                                    <span className="ml-2 text-gray-500">{t('common:loading')}</span>
                                </div>
                            ) : (
                                <>
                                    {/* Table View - Desktop */}
                                    <div className="hidden sm:block rounded-md border border-slate-200 overflow-hidden bg-white">
                                        <Table>
                                            <TableHeader className="bg-slate-50/50">
                                                <TableRow className="hover:bg-transparent">
                                                    <TableHead className="w-[120px] font-bold text-slate-600">{t('accounts:clients.table.account')}</TableHead>
                                                    <TableHead className="font-bold text-slate-600">{t('accounts:clients.table.client')}</TableHead>
                                                    <TableHead className="text-right font-bold text-slate-600">{t('accounts:clients.table.balance')}</TableHead>
                                                    <TableHead className="font-bold text-slate-600">{t('accounts:clients.table.status')}</TableHead>
                                                    <TableHead className="font-bold text-slate-600">{t('accounts:clients.table.date')}</TableHead>
                                                    <TableHead className="text-right font-bold text-slate-600">{t('accounts:clients.table.actions')}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredComptes.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={6} className="h-40 text-center text-slate-400">
                                                            {searchTerm ? t('accounts:clients.table.noResults') : t('accounts:clients.table.noAccounts')}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    filteredComptes.map((compte) => (
                                                        <TableRow key={compte.id} className="hover:bg-slate-50/50 transition-colors">
                                                            <TableCell className="py-4">
                                                                <div className="space-y-1">
                                                                    <div className="font-mono font-bold text-slate-700">
                                                                        {compte.code}
                                                                    </div>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <Badge variant={typeBadgeVariant(compte.type)} className="text-[10px] px-1.5 h-4 font-bold uppercase tracking-wider">
                                                                            {compte.type}
                                                                        </Badge>
                                                                        <span className="text-[10px] font-bold text-slate-400">{compte.devise}</span>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                {compte.client ? (
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-bold">
                                                                            {compte.client.prenom?.charAt(0)}{compte.client.nom?.charAt(0)}
                                                                        </div>
                                                                        <div>
                                                                            <div className="font-bold text-slate-900 leading-none mb-0.5">
                                                                                {compte.client.prenom} {compte.client.nom}
                                                                            </div>
                                                                            {compte.client.surnom && (
                                                                                <div className="text-xs text-blue-500 font-medium leading-none mb-0.5">
                                                                                    @{compte.client.surnom}
                                                                                </div>
                                                                            )}
                                                                            {compte.agentId && (
                                                                                <div className="text-xs text-emerald-600 font-medium leading-none flex items-center gap-1 mb-0.5">
                                                                                    <UserCog className="h-3 w-3" />
                                                                                    <span>{agentMap[compte.agentId] ?? 'Ajan asiye'}</span>
                                                                                </div>
                                                                            )}
                                                                            <div className="text-xs text-slate-400">
                                                                                {compte.client.telephone || compte.client.email || '-'}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-slate-300">-</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <BalanceCell
                                                                    compte={compte}
                                                                    formatBalance={formatBalance}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge variant={statusBadgeVariant(compte.statut)} className="font-bold px-2">
                                                                    {compte.statut}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="text-xs text-slate-500">
                                                                {formatDate(compte.createdAt)}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <DropdownMenuActions
                                                                    compte={compte}
                                                                    handleViewDetails={handleViewDetails}
                                                                    handleDeactivateCompte={handleDeactivateCompte}
                                                                    handleActivateCompte={handleActivateCompte}
                                                                    handleEditClient={handleEditClient}
                                                                    handleAddAccount={handleAddAccount}
                                                                    handleSendPinLink={handleSendPinLink}
                                                                    openSuspendDialog={openSuspendDialog}
                                                                    handleAssignAgent={handleAssignAgent}
                                                                    isSendingPin={isSendingPin}
                                                                    t={t}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>

                                    {/* Card View - Mobile */}
                                    <div className="block sm:hidden space-y-3">
                                        {filteredComptes.length === 0 ? (
                                            <div className="py-12 text-center bg-white rounded-lg border border-slate-200">
                                                <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                                                <p className="text-slate-500 font-medium">
                                                    {searchTerm ? t('accounts:clients.table.noResults') : t('accounts:clients.table.noAccounts')}
                                                </p>
                                            </div>
                                        ) : (
                                            filteredComptes.map((compte) => (
                                                <div key={compte.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 active:bg-slate-50 transition-colors">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-sm font-bold shadow-sm">
                                                                {compte.client?.prenom?.charAt(0)}{compte.client?.nom?.charAt(0)}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <h4 className="font-bold text-slate-900 truncate">
                                                                    {compte.client?.prenom} {compte.client?.nom}
                                                                </h4>
                                                                {compte.client?.surnom && (
                                                                    <p className="text-[10px] text-blue-500 font-medium truncate">@{compte.client.surnom}</p>
                                                                )}
                                                                {compte.agentId && (
                                                                    <p className="text-[10px] text-emerald-600 font-medium">
                                                                        <UserCog className="inline h-2.5 w-2.5 mr-0.5" />
                                                                        {agentMap[compte.agentId] ?? 'Ajan asiye'}
                                                                    </p>
                                                                )}
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <span className="text-[10px] font-mono font-bold text-slate-500">{compte.code}</span>
                                                                    <Badge variant={typeBadgeVariant(compte.type)} className="text-[8px] h-3.5 px-1 font-bold">
                                                                        {compte.type}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <DropdownMenuActions
                                                            compte={compte}
                                                            handleViewDetails={handleViewDetails}
                                                            handleDeactivateCompte={handleDeactivateCompte}
                                                            handleActivateCompte={handleActivateCompte}
                                                            handleEditClient={handleEditClient}
                                                            handleAddAccount={handleAddAccount}
                                                            handleSendPinLink={handleSendPinLink}
                                                            openSuspendDialog={openSuspendDialog}
                                                            handleAssignAgent={handleAssignAgent}
                                                            isSendingPin={isSendingPin}
                                                            t={t}
                                                        />
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4 pb-4 border-b border-slate-100">
                                                        <div>
                                                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">
                                                                {compte.type === 'BS' ? 'Balans BS' : 'Balans SOL'}
                                                            </p>
                                                            <p className="text-sm font-black text-blue-600">
                                                                {formatBalance(compte.type === 'BS' ? compte.soldeBS : compte.soldeS)}
                                                                <span className="text-[10px] ml-1 opacity-50">{compte.devise}</span>
                                                            </p>
                                                            {compte.type === 'BS' && Number(compte.soldePL) > 0 && (
                                                                <p className="text-[9px] font-bold text-amber-600 mt-1">
                                                                    + {formatBalance(compte.soldePL)} PL
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">Statu</p>
                                                            <div className="flex justify-end">
                                                                <Badge variant={statusBadgeVariant(compte.statut)} className="text-[10px] font-bold uppercase tracking-wider">
                                                                    {compte.statut}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center justify-between pt-3">
                                                        <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-medium">
                                                            <Calendar className="h-3 w-3" />
                                                            {formatDate(compte.createdAt)}
                                                        </div>
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            className="h-auto p-0 text-blue-600 font-bold text-[10px]"
                                                            onClick={() => handleViewDetails(compte)}
                                                        >
                                                            Voir détails <ChevronRight className="h-3 w-3 inline ml-0.5" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* Pagination - Responsive stacking */}
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-2 border-t border-slate-100">
                                        <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-start">
                                            <div className="flex items-center space-x-2">
                                                <p className="text-[10px] sm:text-sm font-bold text-slate-500 uppercase tracking-tight sm:tracking-normal">
                                                    {t('dashboard:filters.chooseDate') === 'Choisir une date' ? 'Liy:' : 'Rows:'}
                                                </p>
                                                <Select
                                                    value={`${itemsPerPage}`}
                                                    onValueChange={(value) => {
                                                        setItemsPerPage(Number(value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <SelectTrigger className="h-8 w-[65px] bg-white border-slate-200 text-xs font-bold">
                                                        <SelectValue placeholder={itemsPerPage} />
                                                    </SelectTrigger>
                                                    <SelectContent side="top" className="min-w-[65px]">
                                                        {[5, 10, 20, 50].map((pageSize) => (
                                                            <SelectItem key={pageSize} value={`${pageSize}`} className="text-xs font-medium">
                                                                {pageSize}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="text-[10px] sm:text-sm font-bold text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">
                                                {totalComptes > 0 ? (
                                                    `${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalComptes)} / ${totalComptes}`
                                                ) : (
                                                    "0 / 0"
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-1.5 w-full sm:w-auto justify-center sm:justify-end">
                                            <Button
                                                variant="outline"
                                                className="hidden xs:flex h-8 w-8 p-0 border-slate-200"
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}
                                            >
                                                <span className="sr-only">{t('common:buttons.previous')}</span>
                                                <ChevronsLeft className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="h-8 px-3 sm:px-2 sm:w-8 border-slate-200 shadow-sm"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                            >
                                                <ChevronLeft className="h-4 w-4 mr-1 sm:mr-0" />
                                                <span className="text-xs font-bold sm:hidden">Precedan</span>
                                            </Button>
                                            <div className="flex items-center justify-center h-8 px-3 bg-blue-600 text-white rounded-md text-xs font-black shadow-md shadow-blue-200 mx-1">
                                                {currentPage}
                                            </div>
                                            <Button
                                                variant="outline"
                                                className="h-8 px-3 sm:px-2 sm:w-8 border-slate-200 shadow-sm"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages || totalPages === 0}
                                            >
                                                <span className="text-xs font-bold sm:hidden">Swivan</span>
                                                <ChevronRight className="h-4 w-4 ml-1 sm:ml-0" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="hidden xs:flex h-8 w-8 p-0 border-slate-200"
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages || totalPages === 0}
                                            >
                                                <span className="sr-only">{t('common:buttons.next')}</span>
                                                <ChevronsRight className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Create Client Modal */}
            <CreateClientModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={() => {
                    setCurrentPage(1);
                    fetchComptes();
                }}
            />

            {/* Add Account Modal for existing client */}
            <CreateClientModal
                isOpen={isAddAccountModalOpen}
                onClose={() => {
                    setIsAddAccountModalOpen(false);
                    setSelectedClientForAccount(null);
                }}
                onSuccess={() => {
                    setIsAddAccountModalOpen(false);
                    setSelectedClientForAccount(null);
                    fetchComptes();
                }}
                existingClient={selectedClientForAccount}
            />

            <EditClientModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setClientToEdit(null);
                    setCompteToEdit(null);
                }}
                onSuccess={fetchComptes}
                client={clientToEdit}
                compte={compteToEdit}
            />

            {/* Suspend Client Confirmation Dialog */}
            <AlertDialog open={isSuspendDialogOpen} onOpenChange={setIsSuspendDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('accounts:clients.dialogs.suspend.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('accounts:clients.dialogs.suspend.message', { name: `${clientToSuspend?.prenom} ${clientToSuspend?.nom}` })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setClientToSuspend(null)}>
                            {t('common:buttons.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleSuspendClient}
                            className="bg-red-500 hover:bg-red-600"
                        >
                            {t('accounts:clients.dialogs.suspend.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* View Details Modal - Compact & Responsive UI */}
            <Dialog open={isViewDetailsOpen} onOpenChange={setIsViewDetailsOpen}>
                <DialogContent className="w-[95vw] sm:max-w-md p-0 overflow-hidden border-none bg-slate-50 shadow-2xl rounded-xl">
                    <DialogHeader className="p-4 bg-white border-b flex flex-row items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                            <Shield className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <DialogTitle className="text-sm font-bold text-slate-900 leading-none mb-1 truncate">
                                {t('accounts:clients.dialogs.details.title')}
                            </DialogTitle>
                            <DialogDescription className="text-[10px] text-slate-500 leading-none truncate">
                                {t('accounts:clients.description')}
                            </DialogDescription>
                        </div>
                    </DialogHeader>

                    {selectedCompteForDetails && (
                        <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
                            {/* Client Section - Responsive */}
                            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                                <div className="p-3 sm:p-4">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shadow-sm shrink-0">
                                            {selectedCompteForDetails.client?.prenom?.charAt(0)}{selectedCompteForDetails.client?.nom?.charAt(0)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-sm font-bold text-slate-900 truncate">
                                                {selectedCompteForDetails.client?.prenom} {selectedCompteForDetails.client?.nom}
                                            </h3>
                                            {selectedCompteForDetails.client?.surnom && (
                                                <p className="text-[10px] text-blue-600 font-medium truncate">@{selectedCompteForDetails.client.surnom}</p>
                                            )}
                                        </div>
                                        <div className="flex gap-1.5 shrink-0">
                                            <div className="h-7 w-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:text-blue-600 transition-colors">
                                                <Phone className="h-3.5 w-3.5" />
                                            </div>
                                            <div className="h-7 w-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:text-blue-600 transition-colors">
                                                <Mail className="h-3.5 w-3.5" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="p-2 rounded bg-slate-50 border border-slate-100/50">
                                            <p className="text-[9px] uppercase font-bold text-slate-400 mb-0.5">{t('common:labels.phone')}</p>
                                            <p className="text-xs font-semibold text-slate-700 truncate">{selectedCompteForDetails.client?.telephone || '-'}</p>
                                        </div>
                                        <div className="p-2 rounded bg-slate-50 border border-slate-100/50">
                                            <p className="text-[9px] uppercase font-bold text-slate-400 mb-0.5">{t('common:labels.email')}</p>
                                            <p className="text-xs font-semibold text-slate-700 truncate">{selectedCompteForDetails.client?.email || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Info Grid - Responsive columns */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* Account Box */}
                                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 flex flex-col justify-between space-y-2">
                                    <div className="flex items-center gap-1.5 opacity-60">
                                        <CreditCard className="h-3 w-3" />
                                        <span className="text-[9px] font-bold uppercase tracking-wider">{t('accounts:clients.table.account')}</span>
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] text-slate-500">Kòd</span>
                                            <span className="text-[10px] font-mono font-bold text-slate-700">{selectedCompteForDetails.code}</span>
                                        </div>
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="text-[10px] text-slate-500 shrink-0">Tip</span>
                                            <Badge variant={typeBadgeVariant(selectedCompteForDetails.type)} className="text-[8px] h-4 px-1.5 shrink-0">
                                                {selectedCompteForDetails.type}
                                            </Badge>
                                        </div>
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="text-[10px] text-slate-500 shrink-0">Statu</span>
                                            <Badge variant={statusBadgeVariant(selectedCompteForDetails.statut)} className="text-[8px] h-4 px-1.5 font-bold shrink-0">
                                                {selectedCompteForDetails.statut}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>

                                {/* Balance Box */}
                                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 flex flex-col justify-between space-y-2">
                                    <div className="flex items-center gap-1.5 opacity-60">
                                        <Wallet className="h-3 w-3" />
                                        <span className="text-[9px] font-bold uppercase tracking-wider">Balans</span>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[9px] text-slate-400 uppercase font-bold">{t('dashboard:balance.available')}</p>
                                        <div className="text-lg font-black text-blue-600 leading-tight">
                                            {formatBalance(selectedCompteForDetails.soldeBS)}
                                            <span className="text-[10px] font-bold ml-0.5 opacity-60 uppercase">{selectedCompteForDetails.devise}</span>
                                        </div>
                                        {selectedCompteForDetails.soldePL !== undefined && selectedCompteForDetails.soldePL > 0 && (
                                            <p className="text-[9px] font-bold text-amber-600 mt-0.5">
                                                + {formatBalance(selectedCompteForDetails.soldePL)} PL
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* SOL Details - Responsive columns */}
                            {selectedCompteForDetails.type === 'SOL' && (
                                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow-sm border border-indigo-100 p-3">
                                    <div className="flex items-center gap-1.5 mb-2 text-indigo-700">
                                        <CalendarDays className="h-3 w-3" />
                                        <span className="text-[9px] font-bold uppercase tracking-wider">Plan Solidè</span>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                        <div className="text-center bg-white/50 p-1.5 rounded border border-white/50">
                                            <p className="text-[8px] uppercase font-bold text-slate-400">Frekans</p>
                                            <p className="text-[10px] font-bold text-indigo-900">{selectedCompteForDetails.frequence || '-'}</p>
                                        </div>
                                        <div className="text-center bg-white/50 p-1.5 rounded border border-white/50">
                                            <p className="text-[8px] uppercase font-bold text-slate-400">Jou</p>
                                            <p className="text-[10px] font-bold text-indigo-900">{selectedCompteForDetails.duree || '-'}</p>
                                        </div>
                                        <div className="text-center bg-white/50 p-1.5 rounded border border-white/50 col-span-2 sm:col-span-1">
                                            <p className="text-[8px] uppercase font-bold text-slate-400">Montant pa jou</p>
                                            <p className="text-[10px] font-bold text-indigo-900">
                                                {formatBalance(selectedCompteForDetails.montantJours)}
                                                <span className="text-[8px] ml-0.5 opacity-50">{selectedCompteForDetails.devise}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Footer Info */}
                            <div className="flex items-center justify-between px-1 text-[9px] text-slate-400 font-medium">
                                <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3 opacity-60" />
                                    <span>{formatDate(selectedCompteForDetails.createdAt)}</span>
                                </div>
                                <span className="opacity-40">#{selectedCompteForDetails.id.substring(0, 8)}</span>
                            </div>
                        </div>
                    )}
                    
                    <div className="p-3 bg-white border-t flex justify-end">
                        <Button 
                            variant="secondary" 
                            size="sm"
                            onClick={() => setIsViewDetailsOpen(false)}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs h-8 px-6 active:scale-95 transition-all shadow-sm"
                        >
                            Fèmen
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Export Type Selection Dialog */}
            {/* Assign Agent Modal */}
            <AssignAgentModal
                isOpen={isAssignAgentOpen}
                onClose={() => {
                    setIsAssignAgentOpen(false);
                    setCompteForAssign(null);
                }}
                compte={compteForAssign}
                onSuccess={(updatedCompte) => {
                    setComptes(prev =>
                        prev.map(c => c.id === updatedCompte.id ? { ...c, ...updatedCompte } : c)
                    );
                }}
            />

            <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Download className="h-5 w-5 text-primary-600" />
                            {t('accounts:clients.export.title')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('accounts:clients.export.title')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>{t('common:labels.type')}</Label>
                            <Select
                                value={exportType}
                                onValueChange={(value: 'all' | 'BS' | 'SOL') => setExportType(value)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('accounts:clients.export.all')}</SelectItem>
                                    <SelectItem value="BS">{t('accounts:clients.export.bs')}</SelectItem>
                                    <SelectItem value="SOL">{t('accounts:clients.export.sol')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setIsExportDialogOpen(false)}>
                                {t('common:buttons.cancel')}
                            </Button>
                            <Button onClick={handleExportPDF}>
                                <Download className="mr-2 h-4 w-4" />
                                {t('common:buttons.send')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
